/**
 * Bill-summary / column-header label helper.
 *
 * Many groundworks tables let the user enter a thickness either as millimetres
 * (e.g. "150") or as metres (e.g. "0.150"). For DISPLAY purposes the bill
 * summary should always show the millimetre value, so any decimal input is
 * multiplied by 1000 before being rendered into the description.
 *
 * IMPORTANT: this is a label-only transform. Rate calculations continue to use
 * the raw stored value — see `getM3ToM2Multiplier` in each itemLibraryIntegration.
 *
 * Examples:
 *   "0.150" → "150"
 *   "0.101" → "101"
 *   "1.5"   → "1500"
 *   "150"   → "150"   (no decimal point, returned untouched)
 *   ""      → ""
 *   "abc"   → "abc"   (non-numeric returned untouched)
 */
export function formatDimensionLabel(raw: string | undefined | null): string {
  if (raw === undefined || raw === null) return '';
  const trimmed = String(raw).trim();
  if (!trimmed) return '';
  // Only transform values that explicitly contain a decimal point — preserves
  // already-mm integer entries (and Excel-imported values) verbatim.
  if (!trimmed.includes('.')) return trimmed;
  const num = parseFloat(trimmed);
  if (!Number.isFinite(num)) return trimmed;
  return String(Math.round(num * 1000));
}

/**
 * Find any "<decimal>mm" pattern inside an arbitrary label string and rewrite
 * it as the millimetre integer. Used to repair persisted column headers and
 * descriptions that were built before the bill-summary normalisation existed
 * (e.g. "0.150mm thick MOT Type 1" → "150mm thick MOT Type 1").
 *
 * Patterns matched:
 *   "0.150mm"  → "150mm"
 *   "0.150 mm" → "150mm"   (single space allowed)
 *   "1.5mm"    → "1500mm"
 *   "150mm"    → "150mm"   (no decimal point — left untouched)
 */
export function normaliseDecimalMmInLabel(label: string | undefined | null): string {
  if (!label) return '';
  return String(label).replace(/(\d*\.\d+)\s*mm/gi, (match, num: string) => {
    const value = parseFloat(num);
    if (!Number.isFinite(value)) return match;
    return `${Math.round(value * 1000)}mm`;
  });
}
